import React, { useState } from 'react'
import { Box, Stack, Tab, Tabs, Typography, alpha } from '@mui/material'
import CheckRoundedIcon from '@mui/icons-material/CheckRounded'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'

import { HOLIDAYS_RADIUS } from '@/src/utils/holidays'

export interface InclusionTab {
    id: 'included' | 'excluded'
    label: string
    items: string[]
}

interface PackageInclusionsProps {
    tabs: InclusionTab[]
    showAllLabel: (count: number) => string
    showLessLabel: string
    isMobile: boolean
}

/** Six fills two columns three rows — enough to show the shape of the list without burying it. */
const PREVIEW_COUNT = 6

const TONE = {
    included: { Icon: CheckRoundedIcon, color: 'success.main' },
    excluded: { Icon: CloseRoundedIcon, color: 'error.main' }
} as const

/**
 * What is and is not in the price, as one answer with two sides.
 *
 * They were two sections apart on the page, so a reader had to scroll past the whole inclusion
 * list to learn what the price left out. Tabs put the two counts next to each other, which is the
 * comparison being made.
 */
const PackageInclusions: React.FC<PackageInclusionsProps> = ({
    tabs,
    showAllLabel,
    showLessLabel,
    isMobile
}) => {
    const [activeId, setActiveId] = useState<InclusionTab['id']>(tabs[0]?.id ?? 'included')
    const [expanded, setExpanded] = useState(false)

    const active = tabs.find(tab => tab.id === activeId) ?? tabs[0]
    if (!active) return null

    return (
        <Stack spacing={isMobile ? 2 : 2.5}>
            <Tabs
                value={active.id}
                onChange={(_event, value: InclusionTab['id']) => {
                    setActiveId(value)
                    // A count that applied to the other list would be wrong on this one.
                    setExpanded(false)
                }}
                sx={{
                    minHeight: 'unset',
                    borderBottom: '1px solid',
                    borderColor: 'borders.default',
                    '& .MuiTabs-indicator': { height: 3, bgcolor: 'primary.main' }
                }}
            >
                {tabs.map(tab => (
                    <Tab
                        key={tab.id}
                        value={tab.id}
                        label={
                            <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                                <span>{tab.label}</span>
                                <Box
                                    component="span"
                                    sx={theme => ({
                                        px: 0.875,
                                        py: '1px',
                                        borderRadius: 1,
                                        fontSize: '0.75rem',
                                        fontWeight: 700,
                                        bgcolor:
                                            tab.id === active.id
                                                ? alpha(theme.palette.primary.main, 0.12)
                                                : 'neutral.100',
                                        color: tab.id === active.id ? 'primary.main' : 'textColors.subheading'
                                    })}
                                >
                                    {tab.items.length}
                                </Box>
                            </Stack>
                        }
                        sx={{
                            minHeight: 'unset',
                            px: 0,
                            mr: 3,
                            pb: 1.25,
                            fontSize: isMobile ? '0.938rem' : '1rem',
                            fontWeight: 600,
                            textTransform: 'none',
                            color: 'textColors.subtitle',
                            '&.Mui-selected': { color: 'textColors.primary', fontWeight: 700 }
                        }}
                    />
                ))}
            </Tabs>

            {/*
             * Both lists stay mounted and the inactive one is hidden with CSS: what a package
             * leaves out is exactly the sort of thing a reader searches the page for, and a
             * crawler should see it too.
             */}
            {tabs.map(tab => {
                const { Icon, color } = TONE[tab.id]
                const isCollapsible = tab.items.length > PREVIEW_COUNT
                const visible = isCollapsible && !expanded ? tab.items.slice(0, PREVIEW_COUNT) : tab.items
                const isActive = tab.id === active.id

                return (
            <Box
                key={tab.id}
                role="tabpanel"
                hidden={!isActive}
                sx={{
                    display: isActive ? 'grid' : 'none',
                    gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
                    borderRadius: HOLIDAYS_RADIUS.NESTED,
                    border: '1px solid',
                    borderColor: 'borders.default',
                    overflow: 'hidden'
                }}
            >
                {visible.map((item, index) => (
                    <Stack
                        key={item}
                        direction="row"
                        spacing={1.5}
                        sx={{
                            alignItems: 'center',
                            px: 2,
                            py: 1.75,
                            // Rules between cells only, so the block reads as one table.
                            borderTop: index >= (isMobile ? 1 : 2) ? '1px solid' : 'none',
                            borderInlineStart: !isMobile && index % 2 === 1 ? '1px solid' : 'none',
                            borderColor: 'borders.default'
                        }}
                    >
                        <Box
                            sx={theme => ({
                                flexShrink: 0,
                                width: 28,
                                height: 28,
                                borderRadius: '50%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                bgcolor: alpha(
                                    tab.id === 'included'
                                        ? theme.palette.success.main
                                        : theme.palette.error.main,
                                    0.12
                                )
                            })}
                        >
                            <Icon sx={{ fontSize: 16, color }} />
                        </Box>
                        <Typography sx={{ fontSize: '0.938rem', color: 'textColors.primary' }}>{item}</Typography>
                    </Stack>
                ))}
            </Box>
                )
            })}

            {active.items.length > PREVIEW_COUNT && (
                <Box
                    component="button"
                    type="button"
                    onClick={() => setExpanded(current => !current)}
                    sx={{
                        alignSelf: 'flex-start',
                        border: 0,
                        p: 0,
                        bgcolor: 'transparent',
                        cursor: 'pointer',
                        color: 'primary.main',
                        fontSize: '0.938rem',
                        fontWeight: 700
                    }}
                >
                    {expanded ? showLessLabel : showAllLabel(active.items.length)}
                </Box>
            )}
        </Stack>
    )
}

export { PackageInclusions }
export default PackageInclusions
